import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X, Camera, Sparkles, Bell, SlidersHorizontal, Store } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { getDisplayName } from "../lib/displayName";
import { Loader } from "../components/Loader";

const SORTS = [
  ["relevance", "الأنسب"], ["newest", "الأحدث"], ["price_asc", "السعر ↑"], ["price_desc", "السعر ↓"],
];

export default function Search() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [specs, setSpecs] = useState([]);
  const [interests, setInterests] = useState([]);
  const [specId, setSpecId] = useState("");
  const [interestId, setInterestId] = useState("");
  const [sort, setSort] = useState("relevance");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => { api("/specializations").then(setSpecs).catch(() => {}); api("/interests").then(setInterests).catch(() => {}); }, []);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (debounced) params.set("q", debounced);
      if (specId) params.set("specialization_id", specId);
      if (interestId) params.set("interest_id", interestId);
      if (sort) params.set("sort", sort);
      const data = await api(`/marketplace/search?${params.toString()}`);
      setStores(data.stores || []); setProducts(data.products || []);
    } catch {} finally { setLoading(false); }
  }, [debounced, specId, interestId, sort]);
  useEffect(() => { search(); }, [search]);

  return (
    <div className="animate-fade-in-up max-w-5xl mx-auto">
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-white rounded-2xl px-4 h-12 border border-[#E8EAF2]">
          <SearchIcon size={20} className="text-[#9CA3AF]" />
          <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث عن منتج أو متجر..." className="flex-1 outline-none bg-transparent" />
          {q && <button data-testid="search-clear" onClick={() => setQ("")}><X size={18} className="text-[#9CA3AF]" /></button>}
        </div>
        <button data-testid="filters-open" onClick={() => setShowFilters((v) => !v)} className="w-12 h-12 rounded-2xl bg-white border border-[#E8EAF2] flex items-center justify-center text-[#FF3D5A]"><SlidersHorizontal size={20} /></button>
      </div>

      <div className="flex gap-2 mt-3">
        <QuickPill icon={Camera} label="بحث بالصور" onClick={() => navigate("/visual-search")} t="qa-visual-search" />
        <QuickPill icon={Sparkles} label="المساعد الذكي" onClick={() => navigate("/assistant")} t="qa-assistant" />
        <QuickPill icon={Bell} label="مراقبة الأسعار" onClick={() => navigate("/watches")} t="qa-watches" />
      </div>

      {showFilters && (
        <div className="mt-3 p-4 bg-white rounded-2xl border border-[#E8EAF2] space-y-3">
          <div>
            <p className="text-sm font-bold mb-1.5">تخصص المتجر</p>
            <select data-testid="filter-spec" value={specId} onChange={(e) => setSpecId(e.target.value)} className="w-full bg-[#F0F2FA] rounded-xl px-3 py-2.5 outline-none">
              <option value="">كل التخصصات</option>
              {specs.map((s) => <option key={s.specialization_id} value={s.specialization_id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <p className="text-sm font-bold mb-1.5">الاهتمام</p>
            <select data-testid="filter-interest" value={interestId} onChange={(e) => setInterestId(e.target.value)} className="w-full bg-[#F0F2FA] rounded-xl px-3 py-2.5 outline-none">
              <option value="">كل الاهتمامات</option>
              {interests.map((i) => <option key={i.interest_id} value={i.interest_id}>{i.name}</option>)}
            </select>
          </div>
          <div className="flex gap-2 flex-wrap">
            {SORTS.map(([k, l]) => <button key={k} data-testid={`sort-${k}`} onClick={() => setSort(k)} className={`px-3 py-2 rounded-xl text-sm font-bold ${sort === k ? "bg-[#FF3D5A] text-white" : "bg-[#F0F2FA] text-[#161629]"}`}>{l}</button>)}
          </div>
        </div>
      )}

      {loading ? <Loader /> : (
        <>
          {stores.length > 0 && (
            <div className="mt-6">
              <h2 className="font-black text-[#161629] mb-3">المتاجر ({stores.length})</h2>
              <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                {stores.map((s) => (
                  <button key={s.user_id} data-testid={`store-${s.user_id}`} onClick={() => navigate(`/seller/${s.user_id}`)} className="w-28 shrink-0 p-3 bg-white rounded-2xl border border-[#E8EAF2] text-center">
                    <div className="w-14 h-14 rounded-full bg-[#FFE5EA] mx-auto flex items-center justify-center overflow-hidden">
                      {s.picture_base64 || s.picture ? <img src={s.picture_base64 || s.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-xl font-black text-[#FF3D5A]">{(s.store_name || s.name || "?")[0]}</span>}
                    </div>
                    <p className="text-xs font-bold mt-2 line-clamp-2">{getDisplayName(s)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          {products.length > 0 && (
            <div className="mt-6">
              <h2 className="font-black text-[#161629] mb-3">المنتجات ({products.length})</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {products.map((p) => <ProductCard key={p.product_id} item={p} promoted={!!p.is_promoted} />)}
              </div>
            </div>
          )}
          {!loading && debounced && stores.length === 0 && products.length === 0 && (
            <div className="text-center py-16 text-[#6B7280]"><Store size={48} className="mx-auto mb-3 text-[#9CA3AF]" />لا توجد نتائج تطابق بحثك</div>
          )}
        </>
      )}
    </div>
  );
}
function QuickPill({ icon: Icon, label, onClick, t }) {
  return <button data-testid={t} onClick={onClick} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#FFE5EA] text-[#FF3D5A] font-bold text-xs sm:text-sm"><Icon size={16} /> {label}</button>;
}
